import React from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { 
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

const ActivationTab = ({ license }) => {
  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification({
        message: "Copied to clipboard",
        className: "bg-admin_primary text-admin_text_inv",
      })
    })
  }

  // Get status config
  const getStatusConfig = (status) => {
    const configs = {
      free: {
        color: "bg-admin_progress/10 text-admin_progress",
        label: "Free",
      },
      active: {
        color: "bg-admin_success/10 text-admin_success",
        label: "Active",
      },
      paused: {
        color: "bg-admin_warning/10 text-admin_warning",
        label: "Paused",
      },
      expired: {
        color: "bg-admin_error/10 text-admin_error",
        label: "Expired",
      },
      revoked: {
        color: "bg-admin_neutral_1 text-admin_text_secondary",
        label: "Revoked",
      },
    }
    return configs[status] || configs.free
  }

  const statusConfig = getStatusConfig(license.status)

  return (
    <div className="space-y-6">
      {/* Activation Code */}
      <div className="bg-admin_paper rounded-xl p-6">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Activation Code
        </Typography>

        <div className="flex items-center space-x-4">
          <div className="bg-admin_canvas p-4 rounded-lg">
            <Typography
              component="span"
              variant="admin_h2"
              className="font-mono text-admin_primary"
            >
              {license.code}
            </Typography>
          </div>

          <Button
            variant="admin_secondary"
            onClick={() => copyToClipboard(license.code)}
          >
            <ClipboardDocumentIcon className="w-5 h-5 mr-2" />
            Copy
          </Button>
        </div>

        <Typography
          variant="admin_txt1"
          className="text-admin_text_secondary mt-3"
        >
          {license.external 
            ? "Share this code with users to activate the license in the partner system"
            : "Share this code with users to activate the license on their devices"
          }
        </Typography>
      </div>

      {/* Activation Instructions */}
      <div className="bg-admin_paper rounded-xl p-6">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Activation Instructions
        </Typography>

        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-admin_primary text-white rounded-full flex items-center justify-center text-sm font-medium">
              1
            </div>

            <Typography 
              variant="admin_txt1" 
              className="text-admin_text"
            >
              {license.external 
                ? "User opens the partner product and navigates to the license activation screen as per their instructions"
                : "User opens the application and navigates to the license activation screen"
              }
            </Typography>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-admin_primary text-white rounded-full flex items-center justify-center text-sm font-medium">
              2
            </div>

            <Typography 
              variant="admin_txt1" 
              className="text-admin_text"
            >
              User enters the activation code:{" "}
              <span className="font-mono bg-admin_canvas px-2 py-1 rounded">
                {license.code}
              </span>
            </Typography>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-admin_primary text-white rounded-full flex items-center justify-center text-sm font-medium">
              3
            </div>

            <Typography 
              variant="admin_txt1" 
              className="text-admin_text"
            >
              {license.external 
                ? "Partner system validates the code and activates the license as per their process"
                : "Application validates the code and activates the license for that device"
              }
            </Typography>
          </div>
        </div>
      </div>

      {/* License Status - Only show for non-external licenses */}
      {!license.external && (
        <div className="bg-admin_paper rounded-xl p-6">
          <Typography 
            component="h2"
            variant="admin_h4" 
            className="mb-4"
          >
            License Status
          </Typography>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col justify-center bg-admin_canvas rounded-lg p-6">
              <div className="mb-0.5">
                {license.status === "active" ? (
                  <CheckCircleIcon className="w-8 h-8 text-admin_success" />
                ) : (
                  <ExclamationTriangleIcon className="w-8 h-8 text-admin_warning" />
                )}
              </div>

              <Typography 
                variant="admin_label" 
                className="font-medium my-0"
              >
                {statusConfig.label}
              </Typography>
            </div>

            <div className="flex flex-col justify-center bg-admin_canvas rounded-lg p-6">
              <Typography 
                component="output"
                variant="admin_h3" 
                className="text-admin_primary"
              >
                {license.devices?.length || 0}
              </Typography>

              <Typography
                variant="admin_label" 
                className="font-normal text-admin_text_secondary my-0"
              >
                Active Devices
              </Typography>
            </div>

            <div className="flex flex-col justify-center p-6 bg-admin_canvas rounded-lg">
              <Typography 
                component="output"
                variant="admin_h3" 
                className="text-admin_text"
              >
                {license.seats - (license.devices?.length || 0)}
              </Typography>

              <Typography
                variant="admin_label" 
                className="font-normal text-admin_text_secondary my-0"
              >
                Available Seats
              </Typography>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default ActivationTab